import { useSelector } from "react-redux";
import { SourceSelectionPageFilesData } from "../../../ImportWizard";
import { SummaryHandlerProps, SummaryHandlerReturnArgs } from "../SummaryPage";
import { ApplicationState } from "../../../../../../../store";
import { useState } from "react";
import ImportProgressBar, { ImportProgressBarProps } from "../components/ImportProgressBar";
import { formatBytes } from "../../../../../../../utilities/helpers";
import { Notifications } from "../../../../../../../utilities/Notifications/Notifications";
import { MediaSource } from "../../../../../../../models/media/MediaSource";
import { FileData } from "../../../../../../../store/api/kinesense";

export default function useHandleFiles(props: SummaryHandlerProps): SummaryHandlerReturnArgs | undefined {
    // Prevent misuse
    if (props.request.source.data.type != "file") {
        throw new Error(
            `This hook should only be used when handling local source files. Current data type: ${props.request.source.data.type}`,
        );
    }

    const { sourceFiles } = props.request.source.data as SourceSelectionPageFilesData;

    const { user } = useSelector((state: ApplicationState) => state);

    const isMultiFileImport = props.request.source.data.sourceFiles.length > 1;

    const [uploadProgressMap, setUploadProgressMap] = useState<Record<string, { file: File; progress: number }>>({});
    function setUploadProgress(fileId: string, file: File, progress: number) {
        setUploadProgressMap((prev) => ({ ...prev, [fileId]: { progress, file } }));
    }

    function resetUploadProgressMap() {
        setUploadProgressMap({});
    }

    const countTotalFiles =
        props.request.source.data?.type == "file" ? props.request.source.data.sourceFiles.length : 0;
    const countFilesUploading = Object.keys(uploadProgressMap).length;

    const totalWeight = sourceFiles.reduce((acc, f) => acc + f.file.size, 0);
    const totalWeightedProgress = Object.values(uploadProgressMap).reduce(
        (acc, v) => acc + v.progress * v.file.size,
        0,
    );
    const totalUploadProgress = totalWeightedProgress / Math.max(totalWeight, 1);

    const uploadStateMessage =
        props.uploadState == "requested"
            ? "Completed"
            : `${isMultiFileImport ? `[${countFilesUploading}/${countTotalFiles}] ` : ""}In progress...`;

    const uploadProgressBar: ImportProgressBarProps = {
        message: `Upload - ${uploadStateMessage}`,
        progress: totalUploadProgress,
    };

    function renderSourceSummaryRow() {
        const source = sourceFiles.map((f) => f.file.name).join("\n");

        return (
            <tr>
                <td>{isMultiFileImport ? "Sources" : "Source"}</td>
                <td className="break" title={source}>
                    {source}
                </td>
            </tr>
        );
    }

    function renderAdditionalSummaryRows() {
        return (
            <>
                {isMultiFileImport && (
                    <tr>
                        <td>File Count</td>
                        <td>{(props.request.source.data as SourceSelectionPageFilesData).sourceFiles.length}</td>
                    </tr>
                )}

                {props.request.source.data?.type === "file" && (
                    <tr>
                        <td>File Size</td>
                        <td>
                            {formatBytes(
                                props.request.source.data.sourceFiles.reduce((acc, f) => acc + f.file.size, 0),
                            )}
                        </td>
                    </tr>
                )}
            </>
        );
    }

    function renderProgressBar() {
        return <ImportProgressBar {...uploadProgressBar} />;
    }

    async function uploadFiles(mediaSource: MediaSource): Promise<FileData[]> {
        if (props.request.source.data?.type !== "file") {
            return;
        }

        props.setUploadState("uploading");

        const files: FileData[] = [];
        // Represents offset from media start time
        let startsAt = 0;
        // Sequentially upload files
        for (const sourceFile of props.request.source.data.sourceFiles) {
            const file = sourceFile.file;
            console.log(`Uploading file: ${file.name}`);

            let fileId: string;
            try {
                fileId = await new Promise<string>((resolve, reject) => {
                    const onError = (e) => {
                        Notifications.notify(
                            "Error with uploading file",
                            "Please try again. If this error continues to occur, please contact customer support.",
                            "warning",
                            "errorAnalysisRequest",
                        );
                        resetUploadProgressMap();
                        reject(e);
                        console.error(e);
                        throw e;
                    };

                    let fileId = "";
                    const uploader = props.createUploader({
                        file,
                        groupId: mediaSource.mediaId,
                        intent: "media",
                        onStart: undefined,
                        onUploadInitialized: (response) => {
                            console.log(`upload initialized for file: ${file.name}`, response);
                            fileId = response.fileId;
                        },
                        onProgress: (progress) => {
                            console.log(`progress for file: ${file.name}`, progress);
                            setUploadProgress(fileId, file, progress);
                        },
                        onComplete: () => {
                            console.log(`completed upload for file: ${file.name}`);
                            setUploadProgress(fileId, file, 100);
                            resolve(fileId);
                        },
                        onError,
                        getUser: () => {
                            return user;
                        },
                    });

                    uploader.start().catch(onError);
                });
            } catch {
                props.setUploadState("failed");
            }

            files.push({ fileId, startsAt });
            // TODO: don't assume that files start right after the previous file finishes
            startsAt += sourceFile.duration;
        }

        return files;
    }

    return {
        setupFiles: uploadFiles,
        resetFiles: resetUploadProgressMap,
        renderSourceSummaryRow,
        renderAdditionalSummaryRows,
        renderProgressBar,
    };
}
